/**
 * In-memory evaluator for the `CustomFieldQueryParser` expression language, used
 * to decide in real time whether a dependent custom field should be visible on
 * a document. Mirrors the backend evaluator in
 * `backend/documents/custom_field_values.py`.
 *
 * Supported operators: AND, OR, NOT, exact, in, isnull, exists, icontains,
 * istartswith, iendswith, gt, gte, lt, lte, range, contains.
 */

export type CustomFieldValues = Record<number | string, any>

const toBool = (value: any): boolean => {
  if (typeof value === 'string') {
    return ['true', '1', 'yes'].includes(value.trim().toLowerCase())
  }
  return Boolean(value)
}

const isPresent = (value: any): boolean => {
  return value !== null && value !== undefined && value !== ''
}

const lookupValue = (values: CustomFieldValues, idOrName: any): any => {
  if (idOrName in values) {
    return values[idOrName]
  }
  if (typeof idOrName === 'number' && String(idOrName) in values) {
    return values[String(idOrName)]
  }
  if (typeof idOrName === 'string' && /^\d+$/.test(idOrName) && Number(idOrName) in values) {
    return values[Number(idOrName)]
  }
  return null
}

const evaluateAtom = (idOrName: any, rawOp: string, expected: any, values: CustomFieldValues): boolean => {
  const actual = lookupValue(values, idOrName)

  const op = String(rawOp).includes('__') ? String(rawOp).split('__').pop()! : String(rawOp)

  if (op === 'exists') {
    return isPresent(actual) === toBool(expected)
  }
  if (op === 'isnull') {
    return !isPresent(actual) === toBool(expected)
  }

  if (!isPresent(actual)) {
    return false
  }

  switch (op) {
    case 'exact':
      if (typeof actual === 'boolean') {
        return actual === toBool(expected)
      }
      return String(actual) === String(expected)
    case 'in':
      return Array.isArray(expected) && expected.map((e) => String(e)).includes(String(actual))
    case 'icontains':
      return String(actual).toLowerCase().includes(String(expected).toLowerCase())
    case 'istartswith':
      return String(actual).toLowerCase().startsWith(String(expected).toLowerCase())
    case 'iendswith':
      return String(actual).toLowerCase().endsWith(String(expected).toLowerCase())
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const left = Number(actual)
      const right = Number(expected)
      const numeric = !Number.isNaN(left) && !Number.isNaN(right)
      const a: any = numeric ? left : actual
      const b: any = numeric ? right : expected
      if (op === 'gt') return a > b
      if (op === 'gte') return a >= b
      if (op === 'lt') return a < b
      return a <= b
    }
    case 'range': {
      if (!Array.isArray(expected) || expected.length !== 2) return false
      const value = Number(actual)
      const lo = Number(expected[0])
      const hi = Number(expected[1])
      if (![value, lo, hi].some((n) => Number.isNaN(n))) {
        return lo <= value && value <= hi
      }
      return expected[0] <= actual && actual <= expected[1]
    }
    case 'contains': {
      const actualSet = Array.isArray(actual) ? actual.map((v) => String(v)) : [String(actual)]
      const expectedSet = Array.isArray(expected) ? expected.map((v) => String(v)) : [String(expected)]
      return expectedSet.every((v) => actualSet.includes(v))
    }
    default:
      return false
  }
}

/**
 * Evaluate a visibility query against the current custom field values.
 * A null/empty query means "always visible" and returns true.
 */
export const evaluateCustomFieldQuery = (query: any, values: CustomFieldValues): boolean => {
  if (query === null || query === undefined || query === '') {
    return true
  }

  let parsed = query
  if (typeof query === 'string') {
    try {
      parsed = JSON.parse(query)
    } catch {
      return true
    }
  }

  if (!Array.isArray(parsed)) {
    return true
  }

  if (parsed.length === 2) {
    const [op, args] = parsed
    const opLower = String(op).toLowerCase()
    if (opLower === 'not') {
      return !evaluateCustomFieldQuery(args, values)
    }
    if (opLower === 'and') {
      return (Array.isArray(args) ? args : [args]).every((arg) => evaluateCustomFieldQuery(arg, values))
    }
    if (opLower === 'or') {
      return (Array.isArray(args) ? args : [args]).some((arg) => evaluateCustomFieldQuery(arg, values))
    }
    return true
  }

  if (parsed.length === 3) {
    return evaluateAtom(parsed[0], parsed[1], parsed[2], values)
  }

  return true
}

/**
 * Given the configs (with `visibility_query` and `custom_field`) and the current
 * field values, return the set of field ids that should be HIDDEN. Runs to a
 * fixpoint so chained dependencies (A -> B -> C) collapse correctly: a hidden
 * parent is treated as absent for its dependents.
 */
export const computeHiddenFieldIds = (configs: { custom_field: number; visibility_query?: any }[], values: CustomFieldValues): Set<number> => {
  const hidden = new Set<number>()
  const working: CustomFieldValues = { ...values }
  const queryConfigs = configs.filter((c) => c.visibility_query !== null && c.visibility_query !== undefined)

  let changed = true
  while (changed) {
    changed = false
    for (const config of queryConfigs) {
      const fieldId = config.custom_field
      if (hidden.has(fieldId)) continue
      if (!evaluateCustomFieldQuery(config.visibility_query, working)) {
        hidden.add(fieldId)
        working[fieldId] = null
        changed = true
      }
    }
  }
  return hidden
}
